import type { Camera } from '../renderer/Camera';
import type { FleetState } from '../systems/FleetState';
import type { FleetRenderer } from '../objects/FleetRenderer';
import type { DebugCircleRenderer } from '../objects/DebugCircleRenderer';
import { SHIP_TYPE_DEFS, SHIP_TYPE_COUNT, SHIP_CLASS_NAMES } from '../config/ShipTypes';

/**
 * Shows a single ship with its footprint circle and a radius slider.
 * Prev/Next buttons cycle through all ship types.
 */
export class FootprintDebugTab {
  private _fleet: FleetState;
  private _fleetRenderer: FleetRenderer;
  private _circle: DebugCircleRenderer;
  private _root: HTMLDivElement;
  private _label!: HTMLDivElement;
  private _slider!: HTMLInputElement;
  private _val!: HTMLSpanElement;
  private _idx = 0;

  constructor(
    fleet: FleetState, fleetRenderer: FleetRenderer, circle: DebugCircleRenderer,
    parent: HTMLElement,
  ) {
    this._fleet = fleet;
    this._fleetRenderer = fleetRenderer;
    this._circle = circle;
    this._root = document.createElement('div');
    Object.assign(this._root.style, { padding: '8px 14px 12px', display: 'none' });
    this._buildContent();
    parent.appendChild(this._root);
  }

  get root(): HTMLDivElement { return this._root; }
  get currentIdx(): number { return this._idx; }

  show(): void { this._root.style.display = ''; }
  hide(): void { this._root.style.display = 'none'; }

  activate(): void {
    this._showShip(this._idx);
  }

  deactivate(): void {
    this._circle.setActive(false);
  }

  updateView(cam: Camera, x: number, z: number): void {
    const fp = SHIP_TYPE_DEFS[this._idx].footprint;
    const dist = Math.max(0.3, fp * 2.5);
    cam.target.y = 0;
    cam.position.x = x;
    cam.position.y = dist * 0.7;
    cam.position.z = z + dist;
    const f = this._fleetRenderer.formation;
    if (f.total > 0) {
      this._circle.setSingle(f.px[0], f.py[0], f.pz[0], fp);
    } else {
      this._circle.setSingle(x, 0, z, fp);
    }
  }

  private _showShip(idx: number): void {
    this._idx = idx;
    const def = SHIP_TYPE_DEFS[idx];
    this._label.textContent = `${def.name} (${SHIP_CLASS_NAMES[def.shipClass]})`;
    this._slider.value = String(def.footprint);
    this._val.textContent = def.footprint.toFixed(3);

    const comp = new Array(SHIP_TYPE_COUNT).fill(0);
    comp[idx] = 1;
    this._fleet.setComposition(comp);
    this._fleetRenderer.lineup = false;
    this._circle.setActive(true);
    this._circle.setColor(0.2, 0.9, 0.4);
  }

  private _buildContent(): void {
    const btnCss = 'background:#223;color:#8cf;border:none;cursor:pointer;'
      + 'font-size:14px;padding:2px 8px;border-radius:4px;';

    const nav = this._div({ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' });
    const prev = document.createElement('button');
    prev.textContent = '◀';
    prev.style.cssText = btnCss;
    const next = document.createElement('button');
    next.textContent = '▶';
    next.style.cssText = btnCss;
    this._label = this._div({ color: '#8cf', flex: '1', textAlign: 'center', fontSize: '13px' });
    prev.addEventListener('click', () => this._showShip((this._idx + SHIP_TYPE_COUNT - 1) % SHIP_TYPE_COUNT));
    next.addEventListener('click', () => this._showShip((this._idx + 1) % SHIP_TYPE_COUNT));
    nav.appendChild(prev);
    nav.appendChild(this._label);
    nav.appendChild(next);
    this._root.appendChild(nav);

    const lbl = this._div({ color: '#aaa', fontSize: '11px', marginBottom: '2px' });
    lbl.textContent = 'Footprint Radius';
    this._root.appendChild(lbl);

    const row = this._div({ display: 'flex', alignItems: 'center', gap: '6px' });
    this._slider = document.createElement('input');
    this._slider.type = 'range';
    this._slider.min = '0.01';
    this._slider.max = '1.0';
    this._slider.step = '0.005';
    this._slider.value = '0.1';
    this._slider.style.flex = '1';

    this._val = document.createElement('span');
    this._val.textContent = '0.100';
    Object.assign(this._val.style, {
      width: '50px', textAlign: 'right', fontFamily: 'monospace', fontSize: '11px',
    });

    this._slider.addEventListener('input', () => {
      const v = parseFloat(this._slider.value);
      SHIP_TYPE_DEFS[this._idx].footprint = v;
      this._val.textContent = v.toFixed(3);
    });

    row.appendChild(this._slider);
    row.appendChild(this._val);
    this._root.appendChild(row);
  }

  private _div(style: Record<string, string>): HTMLDivElement {
    const d = document.createElement('div');
    Object.assign(d.style, style);
    return d;
  }
}
